"use client";

import { useEffect, useState } from "react";
import { useInstallTarget, withUtm } from "../lib/install";
import { BrowserIcon } from "./BrowserIcon";

// Root-relative anchors so the nav works from any route, not just "/".
const LINKS = [
  { href: "/#features", label: "Features", title: "See 8Dfy features" },
  { href: "/#how", label: "How it works", title: "See how 8Dfy works" },
  // { href: "/tools/instrumental-enhancer", label: "Enhancer" },
  { href: "/tools/8d-audio-converter", label: "8D Converter", title: "Open the free 8D audio converter tool" },
  { href: "/#pricing", label: "Pricing", title: "See 8Dfy pricing" },
  { href: "/#faq", label: "FAQ", title: "See frequently asked questions" },
];

export default function Navbar() {
  const install = useInstallTarget();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll(); // deep links / mid-page reloads start scrolled — set state immediately
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu on Escape and lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <nav
      aria-label="Primary"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-[#06060C]/90 backdrop-blur-xl border-b border-white/[0.055]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1200px] mx-auto flex items-center justify-between px-6 py-4">
        <a href="/" aria-label="8Dfy home" title="8Dfy home" className="flex items-center gap-2">
          <span className="text-xl font-black tracking-tight bg-gradient-to-r from-[#FF3366] to-[#FF9F1C] bg-clip-text text-transparent">
            8Dfy
          </span>
        </a>

        {/* Desktop links */}
        <div className="hidden sm:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              title={l.title}
              className="link-underline text-sm font-bold text-[#b3b3b3] hover:text-white transition-colors duration-200 uppercase tracking-[1.4px]"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={withUtm(install.baseUrl, "navbar")}
            target="_blank"
            rel="noopener noreferrer"
            title={`Install 8Dfy from the ${install.storeName}`}
            className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[1.4px] px-5 py-3 rounded-full border border-[#4d4d4d] text-[#b3b3b3] hover:text-white hover:border-[#FF3366]/60 hover:bg-[#FF3366]/10 transition-all duration-200"
          >
            <BrowserIcon browser={install.browser} className="w-3.5 h-3.5" />
            Install Free
          </a>

          {/* Mobile menu button */}
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen(!open)}
            className="sm:hidden flex flex-col items-center justify-center w-11 h-11 -mr-2 rounded-full text-[#b3b3b3] hover:text-white transition-colors duration-200"
          >
            <span
              className={`block w-[18px] h-[1.5px] bg-current rounded-full transition-transform duration-300 ${
                open ? "translate-y-[3px] rotate-45" : ""
              }`}
            />
            <span
              className={`block w-[18px] h-[1.5px] bg-current rounded-full mt-[5px] transition-transform duration-300 ${
                open ? "-translate-y-[3.5px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <div
        id="mobile-menu"
        className={`sm:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          open ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 pb-6 pt-2 flex flex-col gap-1 border-t border-white/[0.055]">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              title={l.title}
              onClick={() => setOpen(false)}
              className="py-3 text-sm font-bold text-[#b3b3b3] hover:text-white transition-colors duration-200 uppercase tracking-[1.4px]"
            >
              {l.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}
